import {FileInputType} from '@ui/utils/files/file-input-config';
import {normalizeStorageUrl} from '@ui/utils/urls/normalize-storage-url';

export type MediaImageFormat =
  | 'square'
  | 'landscape'
  | 'poster'
  | 'portrait';

export interface MediaImageUploadRequirement {
  format: MediaImageFormat;
  minWidth: number;
  minHeight: number;
  aspectRatio: number;
  aspectRatioLabel: '1:1' | '16:9' | '4:5' | '9:16';
}

interface MediaImageSource {
  image?: string;
  image_format?: string;
  artwork?: string;
  artwork_large?: string;
  thumb_16_9?: string;
  thumb_1080?: string;
  thumb_4_5?: string;
  thumb_9_16?: string;
}

interface MediaWithType extends MediaImageSource {
  type?: {
    slug?: string;
  };
}

interface TrackLike extends MediaImageSource {
  album?: MediaWithType;
}

const DEFAULT_ALBUM_IMAGE_PATH = 'storage/default/album.png';
const MEDIA_FIELDS: Array<keyof MediaImageSource> = [
  'image',
  'artwork',
  'artwork_large',
  'thumb_16_9',
  'thumb_1080',
  'thumb_4_5',
  'thumb_9_16',
];

const VALID_FORMATS = new Set<MediaImageFormat>([
  'square',
  'landscape',
  'poster',
  'portrait',
]);

const UPLOAD_REQUIREMENTS: Record<MediaImageFormat, MediaImageUploadRequirement> =
  {
    square: {
      format: 'square',
      minWidth: 1280,
      minHeight: 1280,
      aspectRatio: 1,
      aspectRatioLabel: '1:1',
    },
    landscape: {
      format: 'landscape',
      minWidth: 2276,
      minHeight: 1280,
      aspectRatio: 16 / 9,
      aspectRatioLabel: '16:9',
    },
    poster: {
      format: 'poster',
      minWidth: 1280,
      minHeight: 1600,
      aspectRatio: 4 / 5,
      aspectRatioLabel: '4:5',
    },
    portrait: {
      format: 'portrait',
      minWidth: 1280,
      minHeight: 2276,
      aspectRatio: 9 / 16,
      aspectRatioLabel: '9:16',
    },
  };

export function normalizeMediaImageFormat(
  format?: string | null,
): MediaImageFormat | undefined {
  if (!format) {
    return undefined;
  }

  const aliases: Record<string, MediaImageFormat> = {
    '1:1': 'square',
    '16:9': 'landscape',
    '4:5': 'poster',
    '9:16': 'portrait',
  };

  const normalized =
    aliases[format.trim().toLowerCase()] ?? format.trim().toLowerCase();

  return VALID_FORMATS.has(normalized as MediaImageFormat)
    ? (normalized as MediaImageFormat)
    : undefined;
}

export function getMediaImageFormatForContentType(
  contentType?: string | null,
): MediaImageFormat {
  const normalized = normalizeContentType(contentType);

  switch (normalized) {
    case 'music_video':
    case 'podcast_video':
    case 'video_podcast':
    case 'video':
      return 'landscape';
    case 'podcast':
    case 'audiobook':
    case 'audio_book':
      return 'poster';
    default:
      return 'square';
  }
}

export function getDefaultImageFormatForInputTypes(
  inputTypes?: FileInputType[],
): MediaImageFormat {
  return inputTypes?.includes(FileInputType.video) ? 'landscape' : 'square';
}

export function getMediaImageAspectClass(format: MediaImageFormat): string {
  switch (format) {
    case 'landscape':
      return 'aspect-[16/9]';
    case 'poster':
      return 'aspect-[4/5]';
    case 'portrait':
      return 'aspect-[9/16]';
    default:
      return 'aspect-square';
  }
}

export function getMediaImageUploadRequirement(
  format: MediaImageFormat,
): MediaImageUploadRequirement {
  return UPLOAD_REQUIREMENTS[format];
}

export function getMediaImageUploadDescription(
  format: MediaImageFormat,
  options?: {
    baseLine?: string;
    emptyStateNote?: string;
    generatedVariantsNote?: string;
    joinWith?: string;
  },
): string {
  const requirement = getMediaImageUploadRequirement(format);
  const baseLine =
    options?.baseLine ??
    `Recommended ratio: ${requirement.aspectRatioLabel}. Minimum size: ${requirement.minWidth}x${requirement.minHeight}px.`;
  const lines = [
    baseLine,
  ];

  if (options?.generatedVariantsNote) {
    lines.push(options.generatedVariantsNote);
  }

  if (options?.emptyStateNote) {
    lines.push(options.emptyStateNote);
  }

  return lines.join(options?.joinWith ?? ' ');
}

export function getAlbumImageFormat(
  album?: MediaWithType | null,
): MediaImageFormat {
  return (
    normalizeMediaImageFormat(album?.image_format) ??
    getMediaImageFormatForContentType(album?.type?.slug)
  );
}

export function getTrackImageFormat(track?: TrackLike | null): MediaImageFormat {
  return (
    normalizeMediaImageFormat(track?.image_format) ??
    getAlbumImageFormat(track?.album)
  );
}

export function getAlbumImageSrc(
  album?: MediaWithType | null,
  contentFormat?: string,
): string | undefined {
  const requestedFormat =
    normalizeMediaImageFormat(contentFormat) ?? 'square';

  return album
    ? getFirstAvailableImage(
        getCandidates(album, requestedFormat),
      ) ?? getDefaultMediaImageSrc()
    : getDefaultMediaImageSrc();
}

export function getTrackImageSrc(
  track: TrackLike,
  contentFormat?: string,
): string | undefined {
  const requestedFormat =
    normalizeMediaImageFormat(contentFormat) ?? 'square';

  return (
    getFirstAvailableImage(
      getCandidates(track, requestedFormat),
    ) ?? getAlbumImageSrc(track.album, requestedFormat)
  );
}

export function hydrateAlbumMedia<T extends MediaWithType | null | undefined>(
  album: T,
): T {
  if (!album) {
    return album;
  }

  const defaultImage = getDefaultMediaImageSrc();
  const hydrated = {...album};

  MEDIA_FIELDS.forEach(field => {
    hydrated[field] = hydrated[field] ?? defaultImage;
  });

  return hydrated;
}

export function hydrateTrackMedia<T extends TrackLike | null | undefined>(
  track: T,
): T {
  if (!track) {
    return track;
  }

  const hydratedAlbum = hydrateAlbumMedia(track.album);
  const defaultImage = getDefaultMediaImageSrc();
  const hydrated = {
    ...track,
    album: hydratedAlbum,
  };

  MEDIA_FIELDS.forEach(field => {
    hydrated[field] =
      hydrated[field] ?? hydratedAlbum?.[field] ?? defaultImage;
  });

  return hydrated;
}

function getCandidates(
  media: MediaImageSource,
  requestedFormat: MediaImageFormat,
): Array<string | undefined> {
  const sourceImage = media.image;

  switch (requestedFormat) {
    case 'landscape':
      return [
        media.thumb_16_9,
        media.thumb_1080,
        media.artwork_large,
        media.artwork,
        media.thumb_4_5,
        media.thumb_9_16,
        sourceImage,
      ];
    case 'poster':
      return [
        media.thumb_4_5,
        media.thumb_9_16,
        media.thumb_1080,
        media.artwork_large,
        media.artwork,
        media.thumb_16_9,
        sourceImage,
      ];
    case 'portrait':
      return [
        media.thumb_9_16,
        media.thumb_4_5,
        media.thumb_16_9,
        media.thumb_1080,
        media.artwork_large,
        media.artwork,
        sourceImage,
      ];
    default:
      return [
        media.artwork_large,
        media.artwork,
        media.thumb_1080,
        media.thumb_4_5,
        media.thumb_9_16,
        media.thumb_16_9,
        sourceImage,
      ];
  }
}

function getFirstAvailableImage(
  candidates: Array<string | undefined>,
): string | undefined {
  for (const candidate of candidates) {
    if (candidate) {
      return normalizeStorageUrl(candidate);
    }
  }
}

export function getDefaultMediaImageSrc(): string | undefined {
  const normalized = normalizeStorageUrl(DEFAULT_ALBUM_IMAGE_PATH);

  return normalized || undefined;
}

function normalizeContentType(contentType?: string | null): string | undefined {
  if (!contentType) {
    return undefined;
  }

  return contentType.trim().toLowerCase().replace(/-/g, '_');
}
